import classNames from 'classnames';
import { when } from 'mobx';
import type { CSSProperties, JSX } from 'react';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { GroupBase, OptionProps, SingleValueProps } from 'react-select-modern';

import type { CampaignClass } from '@feathr/blackbox';
import { CampaignState } from '@feathr/blackbox';
import { FormElement, UniversalSelect, zIndex } from '@feathr/components';
import CampaignTypeChip from '@feathr/extender/components/CampaignTypeChip';
import { useStore } from '@feathr/extender/state';
import { getIconForAction } from '@feathr/hooks';

import type { TChannel } from '../types';
import type { ICampaignOption } from './DraftCampaignSelect.types';
import { getDisplayCampaignClasses, getMetaCampaignClasses } from './DraftCampaignSelect.utils';

import * as styles from './DraftCampaignSelect.css';

const DEFAULT_PAGE_SIZE = 25;

/**
 * Custom option component for the draft campaign select.
 * Displays campaign name with a CampaignTypeChip on the right.
 */
function CampaignOption<
  IsMulti extends boolean = false,
  Group extends GroupBase<ICampaignOption> = GroupBase<ICampaignOption>,
>(props: OptionProps<ICampaignOption, IsMulti, Group>): JSX.Element {
  const { data, innerProps, isFocused, isSelected, getStyles } = props;
  const baseStyles = getStyles('option', props) as CSSProperties;

  // Remove background, text colors, and padding from inline styles so our CSS classes can control them
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { backgroundColor, color, padding, ...optionStyles } = baseStyles;

  return (
    <div
      {...innerProps}
      className={classNames('react-select__option', styles.option, {
        'react-select__option--is-focused': isFocused,
        'react-select__option--is-selected': isSelected,
        [styles.optionFocused]: isFocused && !isSelected,
        [styles.optionSelected]: isSelected,
      })}
      style={optionStyles}
    >
      <div className={styles.optionContent}>
        <span className={styles.optionName}>{data.name}</span>
        <CampaignTypeChip cls={data.cls} />
      </div>
    </div>
  );
}

/**
 * Custom single value component for the draft campaign select.
 * Displays the selected campaign name with a CampaignTypeChip on the right.
 */
function CampaignSingleValue<
  IsMulti extends boolean = false,
  Group extends GroupBase<ICampaignOption> = GroupBase<ICampaignOption>,
>(props: SingleValueProps<ICampaignOption, IsMulti, Group>): JSX.Element {
  const { data, innerProps, getStyles } = props;

  return (
    <div
      {...innerProps}
      className={classNames('react-select__single-value', styles.singleValue)}
      style={getStyles('singleValue', props) as CSSProperties}
    >
      <span className={styles.singleValueName}>{data.name}</span>
      <CampaignTypeChip cls={data.cls} />
    </div>
  );
}

interface IDraftCampaignSelectProps {
  /** Channel to filter campaigns by */
  channel: TChannel;
  /** Event ID to filter campaigns by */
  eventId: string;
  /** Help text shown below the select */
  helpText?: string;
  /** Label shown above the select */
  label?: string;
  /** Callback when a campaign is selected */
  onChange: (campaignId: string) => void;
  /** Whether the field is optional */
  optional?: boolean;
  /** Placeholder text shown when no option is selected */
  placeholder?: string;
}

/**
 * Select component for choosing a draft campaign from the current project.
 * Only shows campaigns in draft state.
 */
function DraftCampaignSelect({
  channel,
  eventId,
  helpText,
  label,
  onChange,
  optional = false,
  placeholder,
}: Readonly<IDraftCampaignSelectProps>): JSX.Element {
  const { t } = useTranslation();
  const { Campaigns } = useStore();
  const [selectedOption, setSelectedOption] = useState<ICampaignOption | null>(null);

  const loadPagedOptions = useCallback(
    async (
      inputValue: string,
      _prevOptions: readonly ICampaignOption[],
      additional?: { page: number },
    ): Promise<{
      options: readonly ICampaignOption[];
      hasMore: boolean;
      additional: { page: number };
    }> => {
      const page = additional?.page ?? 0;

      const campaignClasses =
        channel === 'meta' ? getMetaCampaignClasses() : getDisplayCampaignClasses();

      const response = Campaigns.list({
        filters: {
          _cls__in: campaignClasses,
          event: eventId,
          is_archived__ne: true,
          state: CampaignState.Draft,
          ...(inputValue ? { name__icontains: inputValue } : {}),
        },
        pagination: {
          page,
          page_size: DEFAULT_PAGE_SIZE,
        },
        ordering: ['-date_created'],
      });

      await when(() => !response.isPending);

      const options: ICampaignOption[] = response.models.map((campaign) => ({
        id: campaign.id,
        name: campaign.get('name') ?? '',
        cls: campaign.get('_cls') as CampaignClass,
      }));

      return {
        options,
        hasMore: response.models.length === DEFAULT_PAGE_SIZE,
        additional: { page: page + 1 },
      };
    },
    [Campaigns, channel, eventId],
  );

  function handleSelect(option: ICampaignOption): void {
    setSelectedOption(option);
    onChange(option.id);
  }

  function noOptionsMessage({ inputValue }: { inputValue: string }): string {
    if (inputValue) {
      return t('No draft campaigns found matching "{{inputValue}}"', { inputValue });
    }
    return t('No draft campaigns available');
  }

  return (
    <FormElement
      helpText={helpText ?? t('Select a draft campaign to add the duplicated creatives to')}
      label={label ?? t('Draft Campaign')}
      optional={optional}
    >
      <UniversalSelect<ICampaignOption>
        cacheOptions={true}
        components={{ Option: CampaignOption, SingleValue: CampaignSingleValue }}
        controlPrefix={getIconForAction('search')}
        defaultAdditional={{ page: 0 }}
        defaultOptions={true}
        isClearable={false}
        isSearchable={true}
        loadPagedOptions={loadPagedOptions}
        menuPortalTarget={document.body}
        menuPosition={'fixed'}
        name={'draft-campaign-select'}
        noOptionsMessage={noOptionsMessage}
        onSelectSingle={handleSelect}
        paginate={true}
        placeholder={placeholder ?? t('Select draft campaign')}
        styles={{
          menuList: (base) => ({ ...base, padding: 'var(--spacing-1-half)' }),
          menuPortal: (base) => ({ ...base, zIndex: zIndex.popover }),
          option: (base) => ({ ...base, marginBottom: 'var(--spacing-1)' }),
        }}
        value={selectedOption}
      />
    </FormElement>
  );
}

export default DraftCampaignSelect;
